import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CardSetTransformationService, CoreService, DialogResult, DialogService, FormNavigationService, SaveDataRequest, SpinnerService } from 'dspx-core';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ErrorService } from '../../services/error.service';
import { AppDataService } from '../../services/app-data.service';
import { HtmlHelperService } from '../../services/html-helper.service';
import { FormDataService, HideableGrid } from '../../services/form-data.service';
import { QuestionPath, ResponseParserService } from '../../services/response-parser.service';
import { CoiFormReview } from '../../interfaces/coi-form-review';
import { API_ENDPOINT_KEYS, API_ENDPOINTS } from '../../constants/api-endpoints.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { APP_DATA_KEYS, FORM_ACTIONS, HIDEABLE_GRIDS, minWaitForDialogToCloseMs, NAV_TAB_NUMBERS, OPTION_SET_CODES, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES, UiDateFormat } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { take } from 'rxjs';
import { DateTime } from 'luxon';
import { FormConfigurationParserService } from '../../services/form-configuration-parser.service';

@Component({
  selector: 'app-approver-coi-form',
  templateUrl: './approver-coi-form.component.html',
  styleUrl: './approver-coi-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ApproverCoiFormComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy {

  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  id = '';
  formServiceEndpoint: string = null;
  templateId: number = TEMPLATES.approverCoiForm;
  versionNo: number = VERSIONS.latest;
  activeDialog: string;
  editing = false;
  isViewRoute = false;
  lastUpdatedTime: string;
  reviewDateExpiryHours: number

  private versionHistoryHideableGrid: HideableGrid;
  private isReturnableForActionWithoutAssessment: boolean;

  constructor(
    protected router: Router,
    protected appDataService: AppDataService,
    protected coreService: CoreService,
    protected formDataService: FormDataService,
    protected htmlHelperService: HtmlHelperService,
    protected dialogService: DialogService,
    private route: ActivatedRoute,
    private errorService: ErrorService,
    private responseParserService: ResponseParserService,
    private spinnerService: SpinnerService,
    private formNavigationService: FormNavigationService,
    private formConfigParserService: FormConfigurationParserService) {
    super(router, appDataService, coreService, formDataService, htmlHelperService, dialogService);
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  ngOnInit(): void {
    this.registerSubscription(
      this.route.paramMap.subscribe((paramMap: ParamMap) => {
        const id = paramMap.get('id');
        if (id) {
          this.id = id;
          this.isViewRoute = this.router.url === `/${ROUTE_CONSTANTS.approverCoiForm.viewPrefix}/${id}`;
          this.formServiceEndpoint = this.isViewRoute
            ? API_ENDPOINT_KEYS.getApproverCoiFormConfigAndResponseReadOnly
            : API_ENDPOINT_KEYS.getApproverCoiFormConfigAndResponse;
          this.editing = true;
        }
        else {
          this.resetDirtyFlagAfterLoading();
        }
      })
    );

    this.formNavigationService.onNavigation().subscribe(event => {
      setTimeout(() => {
        this.overrideHtmlTitleSetByDspxFramework();
      })
    });

    this.coreService.conditionFunctions['isEditing'] = () => this.editing;
    this.coreService.conditionFunctions['areMandatoryFieldsEntered'] = () => this.areMandatoryFieldsEntered();
    this.coreService.conditionFunctions['isSatisfiedWithPlan'] = () => this.isSatisfiedWithPlan();
    this.coreService.conditionFunctions['shouldShowAgreeWithDeclarersAssessmentLabel'] = () => this.shouldShowAgreeWithDeclarersAssessmentLabel();
    this.coreService.conditionFunctions['shouldShowDisagreeWithDeclarersAssessmentLabel'] = () => this.shouldShowDisagreeWithDeclarersAssessmentLabel();
    this.coreService.conditionFunctions['shouldShowMoreInformationDeclarersAssessmentLabel'] = () => this.shouldShowMoreInformationDeclarersAssessmentLabel();

    const canApprove = () => this.isFormEditable && this.hasConsent();
    const canReturnForAction = () => this.isFormEditable && !this.hasConsent();

    this.coreService.applicationFunctions['canApprove'] = () => canApprove();
    this.coreService.applicationFunctions['canReturnForAction'] = () => canReturnForAction();
    this.coreService.applicationFunctions['canReturnForActionWithoutAssessment'] = () => !canReturnForAction() && this.isReturnableForActionWithoutAssessment;
    this.coreService.applicationFunctions['canSave'] = () => this.isFormEditable;
    this.coreService.applicationFunctions['canSwitchToEdit'] = () => this.isViewRoute;

    this.coreService.applicationFunctions['approve'] = () => this.approve(null);
    this.coreService.applicationFunctions['returnForAction'] = () => this.returnForAction(null);
    this.coreService.applicationFunctions['returnForActionWithoutAssessment'] = () => this.returnForActionWithoutAssessment(null);
    this.coreService.applicationFunctions['switchToEdit'] = () => this.switchToEdit(null);
    this.coreService.applicationFunctions['downloadCopy'] = () => this.downloadCopy();
    this.coreService.applicationFunctions['reopenWithComment'] = () => this.openDialogForReopenWithComment();
    this.coreService.applicationFunctions['reopen'] = () => this.reopen();
    this.coreService.applicationFunctions['review'] = () => this.openDialogForReview(null);
    this.coreService.applicationFunctions['validateReviewDate'] = (args: any[]) => this.validateReviewDate(args);

    this.versionHistoryHideableGrid = this.initialiseHideableGrid(HIDEABLE_GRIDS.approverCoiForm.versionHistory);
  }

  formLoaded(event: any): void {
    if (event.retryAsReadOnly) {
      this.navigate(['/', ROUTE_CONSTANTS.approverCoiForm.viewPrefix, this.id], true);
      return;
    }

    this.isFormEditable = !!event?.isFormEditable;
    this.lastUpdatedTime = event.lastUpdatedTime;
    this.reviewDateExpiryHours = event.reviewDateExpiryHours;
    this.isReturnableForActionWithoutAssessment = !!event?.isReturnableForActionWithoutAssessment;

    if (this.baseForm && this.baseForm.model) {
      this.appDataService.setAppData(APP_DATA_KEYS.previousModel, JSON.parse(JSON.stringify(this.baseForm.model)));
    }

    this.versionHistoryHideableGrid.toggle(HIDEABLE_GRIDS.approverCoiForm.versionHistory.showOnFirstLoad);

    this.ensurePageActionButtonsAreEnabledOnFormLoad(READ_ONLY_ACCESS_PAGE_ACTION_BUTTONS.approverCoiForm);
    this.handlePostFormLoadActions(event);
  }

  modelChanged(event: any): void {
    this.handleSingleCheckBoxChanges();
  }

  validateReviewDate(args: any[]): boolean {

    const formControlValue = args?.[0]?.formControl?.value;

    if (formControlValue) {

      const reviewDate = (DateTime.fromFormat(formControlValue, UiDateFormat)).endOf('day');
      const currentDate = DateTime.now();

      const futureReviewDate = currentDate.plus({ hours: this.reviewDateExpiryHours });

      return reviewDate >= futureReviewDate;
    }

    return true;
  }

  switchToEdit(event: any): void {
    this.navigate(['/', ROUTE_CONSTANTS.approverCoiForm.prefix, this.id], true);
  }

  save(event: any): void {
    this.executeSave({
      skipValidation: true,
      apiEndpoint: API_ENDPOINTS.approverCoiForm.save,
      confirmationPopupCode: null,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverCoiForm,
      successPopupCode: POP_UP_CODES.common.saveSuccessful
    });
  }

  approve(event: any): void {
    this.executeSave({
      skipValidation: false,
      apiEndpoint: API_ENDPOINTS.approverCoiForm.approve,
      confirmationPopupCode: POP_UP_CODES.approverCoiForm.approveConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverCoiForm,
      successPopupCode: POP_UP_CODES.approverCoiForm.approveSuccessful
    });
  }

  returnForAction(event: any): void {
    this.executeSave({
      skipValidation: false,
      apiEndpoint: API_ENDPOINTS.approverCoiForm.returnForAction,
      confirmationPopupCode: POP_UP_CODES.approverCoiForm.returnForActionConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverCoiForm,
      successPopupCode: POP_UP_CODES.approverCoiForm.returnedForAction
    });
  }

  returnForActionWithoutAssessment(event: any): void {
    this.executeSave({
      skipValidation: true,
      apiEndpoint: API_ENDPOINTS.approverCoiForm.returnForActionWithoutAssessment,
      confirmationPopupCode: POP_UP_CODES.approverCoiForm.returnForActionConfirmation,
      serverErrorPopupCodes: SERVER_ERROR_POP_UP_CODES.saveApproverCoiForm,
      successPopupCode: POP_UP_CODES.approverCoiForm.returnedForAction
    });
  }

  private executeSave(parameters: { skipValidation: boolean, apiEndpoint: string, confirmationPopupCode: string, serverErrorPopupCodes: string[], successPopupCode: string }): void {
    if (!parameters.skipValidation && !this.baseForm.fields[0]?.formControl.valid) {
      this.baseForm.markAllFieldsAsTouched();
      this.baseForm.showValidationSummary(true);
      this.dialogService.showDialog(POP_UP_CODES.common.validationError);
    }
    else {
      const saveData = () => {
        const saveDataRequest: SaveDataRequest = {
          template: this.templateId,
          versionNumber: this.versionNo,
          id: this.id,
          response: JSON.stringify(this.baseForm.model),
          expectedLastUpdateTime: this.lastUpdatedTime,
          isSubmit: false // not used
        }
        this.spinnerService.showSpinner();
        this.registerSubscription(
          this.appDataService.post(parameters.apiEndpoint, saveDataRequest)
            .pipe(take(1), this.errorService.errorHandler())
            .subscribe(data => {
              this.spinnerService.hideSpinner();
              this.activeDialog = parameters.serverErrorPopupCodes.includes(data.message)
                ? data.message
                : parameters.successPopupCode;
              this.dialogService.showDialog(this.activeDialog);
            }));
      };
      if (!parameters.confirmationPopupCode) {
        saveData();
      }
      else {
        this.registerSubscription(
          this.dialogService
            .showConfirm(parameters.confirmationPopupCode)
            .pipe(take(1))
            .subscribe(data => {
              switch (data as DialogResult) {
                case DialogResult.ACCEPTED: {
                  saveData();
                  break;
                }
                case DialogResult.REJECTED: {
                  break;
                }
              }
            })
        );
      }
    }
  }

  cancel(event: any) {
    this.navigateBackToDashboard();
  }

  dialogSubmitted(event: any): void {

    const activeDialog = this.activeDialog;
    delete this.activeDialog;

    switch (activeDialog) {
      case POP_UP_CODES.common.saveSuccessful:
      case POP_UP_CODES.approverCoiForm.approveSuccessful:
      case POP_UP_CODES.approverCoiForm.returnedForAction: {
        this.navigateBackToDashboard(true);
        break;
      }
      case POP_UP_CODES.approverCoiForm.review: {
        this.review(event);
        break;
      }
      case POP_UP_CODES.approverCoiForm.reopenWithComment: {
        this.reopenWithComment(event);
        break;
      }
      case POP_UP_CODES.approverCoiForm.reopenSuccessful: {
        this.router.navigate(['/', ROUTE_CONSTANTS.coiForm.prefix, this.id]);
        break;
      }
    }
  }

  actionButtonClicked(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.approverCoiForm.versionHistory: {
        switch (event.event.type) {
          case FORM_ACTIONS.approverCoiForm.downloadVersion: {
            this.downloadVersion(event.event.data.id);
            break;
          }
        }
        break;
      }
    }
  }

  private navigateBackToDashboard(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.coiForm.list], ignoreUnsavedChanges);
  }

  private downloadCopy(): void {
    const downloadCopyEndpoint = `${API_ENDPOINTS.coiForm.downloadCopy}/${this.id}`;
    this.appDataService.generatePdf(downloadCopyEndpoint);
  }

  private downloadVersion(versionId: string): void {
    const downloadVersionEndpoint = `${API_ENDPOINTS.coiForm.downloadVersion}/${this.id}/${versionId}`
    this.appDataService.generatePdf(downloadVersionEndpoint);
  }

  private openDialogForReopenWithComment(): void {
    this.activeDialog = POP_UP_CODES.approverCoiForm.reopenWithComment;
    this.dialogService.showDialog(this.activeDialog);
  }

  private reopenWithComment(model: any): void {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.coiForm.reopenWithComment, { id: this.id, approverReopenComment: model.approverReopenComment })
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();
          this.activeDialog = POP_UP_CODES.approverCoiForm.reopenSuccessful;
          this.dialogService.showDialog(this.activeDialog);
        }));
  }

  private reopen(): void {
    this.registerSubscription(
      this.dialogService
        .showConfirm(POP_UP_CODES.approverCoiForm.reopenConfirmation)
        .pipe(take(1))
        .subscribe(data => {
          switch (data as DialogResult) {
            case DialogResult.ACCEPTED: {
              this.spinnerService.showSpinner();
              this.registerSubscription(
                this.appDataService.post(`${API_ENDPOINTS.coiForm.reopen}/${this.id}`, {})
                  .pipe(take(1), this.errorService.errorHandler())
                  .subscribe(data => {
                    this.spinnerService.hideSpinner();
                    this.activeDialog = POP_UP_CODES.approverCoiForm.reopenSuccessful;
                    this.dialogService.showDialog(this.activeDialog);
                  }));
              break;
            }
            case DialogResult.REJECTED: {
              break;
            }
          }
        })
    );
  }

  private handleSingleCheckBoxChanges(): void {
    const consentQuestionPath: QuestionPath = [
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      QUESTION_CODES.approverCoiForm.consent
    ];
    this.responseParserService.handleSingleCheckBoxQuestionChanges(
      () => this.baseForm.model,
      model => this.baseForm.model = model,
      [consentQuestionPath]);
  }

  private areMandatoryFieldsEntered(): boolean {
    var comment = this.getApproverCommentsQuestionModel();
    var needToReview = this.getNeedToReviewModel();
    var reviewDate = this.getReviewDateModel();
    var satisfiedWithPlan = this.getSatisfiedWithSuggestedPlanModel();

    var approverCommentFieldConfig = this.formConfigParserService.findQuestionConfig(
      this.baseForm.fields,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      QUESTION_CODES.approverCoiForm.approverComments)
    
    if ((approverCommentFieldConfig?.props?.required && !comment) || (satisfiedWithPlan && (!needToReview)) || (needToReview === OPTION_SET_CODES.approverCoiForm.yesNo.yes && (!reviewDate))) {
      return false;
    }
    else {
      return true;
    }
  }

  private isSatisfiedWithPlan(): boolean {
    var satisfiedWithPlan = this.getSatisfiedWithSuggestedPlanModel();
    return satisfiedWithPlan === null || satisfiedWithPlan === undefined ||
           satisfiedWithPlan === OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.yes;
  }

  private shouldShowAgreeWithDeclarersAssessmentLabel(): boolean {
    const agreeWithDeclarerModel = this.getAgreeWithDeclarerModel();    
    return agreeWithDeclarerModel === OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.yes &&
           this.isSatisfiedWithPlan();
  }

  private shouldShowDisagreeWithDeclarersAssessmentLabel(): boolean {
    const agreeWithDeclarerModel = this.getAgreeWithDeclarerModel();
    const satisfiedWithPlanModel = this.getSatisfiedWithSuggestedPlanModel();
    return agreeWithDeclarerModel === OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.no ||
           satisfiedWithPlanModel == OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.no;
  }

  private shouldShowMoreInformationDeclarersAssessmentLabel(): boolean {
    const agreeWithDeclarerModel = this.getAgreeWithDeclarerModel();
    const satisfiedWithPlanModel = this.getSatisfiedWithSuggestedPlanModel();
    return agreeWithDeclarerModel === OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.moreInformationNeeded ||
           satisfiedWithPlanModel == OPTION_SET_CODES.approverCoiForm.yesNoMoreInformationNeeded.moreInformationNeeded;
  }

  private getApproverCommentsQuestionModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.approverComments);
  }

  private getNeedToReviewModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.needToReviewThePlan);
  }

  private getReviewDateModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.planReviewDate);
  }

  private getSatisfiedWithSuggestedPlanModel(): string {
    return this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.satisfiedWithSuggestedPlan);
  }

  private getAgreeWithDeclarerModel(): string {
    const agreeWithDeclarerModel = this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.agreeWithDeclarer);
    
    return agreeWithDeclarerModel;
  }

  private hasConsent(): boolean {
    const consentModel = this.responseParserService.findQuestionModel(
      this.baseForm?.model,
      NAV_TAB_NUMBERS.approverCoiForm.navTabPlaceholder,
      PAGE_NUMBERS.approverCoiForm.pagePlaceholder,
      null,
      SECTION_CODES.approverCoiForm.coiFormApproverReviewSection,
      null,
      QUESTION_CODES.approverCoiForm.consent);
    
    return consentModel === true;
  }

  private openDialogForReview(model: CoiFormReview): void {

    if (!this.canDeactivate()) {
      this.dialogService.showSimplePopup(
        '<p>You have unsaved changes in the form.</p><p>The review can only be performed when there are no unsaved changes.</p>',
        'Unsaved Changes');
      return;
    }

    this.activeDialog = POP_UP_CODES.approverCoiForm.review;
    this.dialogService.showDialog(this.activeDialog, model);
  }

  private review(model: CoiFormReview): void {

    model = model ?? {};
    model.coiFormId = this.id;

    // Submit the request to the server
    this.registerSubscription(
      this.appDataService.post(API_ENDPOINTS.approverCoiForm.review, model)
        .pipe(take(1), this.errorService.errorHandler())
        .subscribe(data => {
          this.spinnerService.hideSpinner();

          const isSuccess = !SERVER_ERROR_POP_UP_CODES.reviewCoiForm.includes(data.message);
          this.activeDialog = isSuccess ? POP_UP_CODES.approverCoiForm.reviewSuccessful : data.message;

          setTimeout(
            () => {
              if (isSuccess) {
                this.dialogService.showDialog(this.activeDialog);
                this.reloadPage();
              }
              else {
                this.registerSubscription(
                  this.dialogService
                    .showConfirm(this.activeDialog)
                    .pipe(take(1))
                    .subscribe(data => {
                      setTimeout(
                        () => {
                          this.openDialogForReview(model);
                        },
                        minWaitForDialogToCloseMs);
                    }));
              }
            },
            minWaitForDialogToCloseMs);
        }));
  }
}
